/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import {
  byKeys,
  filterBy,
  mapBy,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';
import config from 'linced-app/config/environment';

export default class MergeDialog extends Component {
  // Services
  @service
  fetch;

  @service
  schoolSchema;

  @service
  session;

  @service
  store;

  @tracked person = null;
  searchResult = null;
  @tracked existingPerson = null;
  onCancel() {}
  onDone() {}
  @tracked
  mergeComplete = false;
  @tracked
  mergedPersonId = null;
  @tracked
  mergeFailed = false;
  @tracked
  merging = false;
  @tracked
  errorMessage = '';
  @tracked step = 0;
  @tracked
  schema = null;

  get differences() {
    if (this.step < 1) {
      // Don't try to generate differences if we haven't done our prep.
      return [];
    }
    let fieldKeys = Object.keys(this.person.fields);
    let fieldDifferences = fieldKeys.reduce((differences, key) => {
      if (isEmpty(this.person.fields.get(`${key}.datatype`))) {
        // Skip anything that's not a field value.
        return differences;
      }
      let personField = this.person.get(`fields.${key}`);
      let existingField = this.existingPerson.get(`fields.${key}`);
      if (
        !isEmpty(existingField) &&
        existingField.get('value') !== personField.get('value')
      ) {
        let selected;
        if (
          isEmpty(existingField.get('value')) &&
          !isEmpty(personField.get('value'))
        ) {
          selected = 'person';
        } else if (
          isEmpty(personField.get('value')) &&
          !isEmpty(existingField.get('value'))
        ) {
          selected = 'existing';
        }
        differences.push({
          key,
          field: this.schoolSchema.peekField(PersonType.CAREGIVER, key),
          personField,
          existingField,
          selected,
        });
      }
      return differences;
    }, []);

    if (!isEmpty(this.schema)) {
      let differentKeys = mapBy(fieldDifferences, 'key');
      let sortedKeys = sortBy(this.schema, 'sequence').reduce((keys, group) => {
        let sortedKeys = mapBy(
          group.fields
            .slice()
            .sort((a, b) =>
              a.sequence > b.sequence ? 1 : a.sequence < b.sequence ? -1 : 0,
            )
            .filter((field) => differentKeys.includes(field.key)),
          'key',
        );
        keys.push(...sortedKeys);
        return keys;
      }, []);
      fieldDifferences.forEach((diff) => {
        diff.sequence = sortedKeys.indexOf(diff.key);
      });
      fieldDifferences = fieldDifferences.sort(byKeys('sequence'));
    }

    return fieldDifferences;
  }

  get extraFields() {
    if (this.step < 1) {
      // Don't try to generate anything if we haven't done our prep.
      return [];
    }
    let fieldKeys = Object.keys(this.person.fields);
    return fieldKeys.reduce((extraFields, key) => {
      if (isEmpty(this.person.fields.get(`${key}.datatype`))) {
        // Skip anything that's not a field value.
        return extraFields;
      }
      let personField = this.person.get(`fields.${key}`);
      let existingField = this.existingPerson.get(`fields.${key}`);
      if (isEmpty(existingField)) {
        extraFields.push({
          key,
          dataType: personField.get('datatype'),
          value: personField.get('value'),
        });
      }
      return extraFields;
    }, []);
  }

  @computed('differences.@each.selected')
  get hasUnresolvedConflicts() {
    return this.differences.some((difference) => isEmpty(difference.selected));
  }

  init() {
    super.init(...arguments);
    if (isEmpty(this.person)) {
      this.errorMessage = 'No person specified.';
      return;
    }
    if (isEmpty(this.searchResult)) {
      this.errorMessage = 'No search result provided.';
    }

    this.schoolSchema.getPeopleSchema().then((schema) => {
      this.schema = filterBy(schema, 'personType', PersonType.CAREGIVER);
    });
    let chain = Promise.resolve();
    if (!this.get('searchResult.inThisSchool')) {
      chain = chain.then(() => {
        return this.enableCaregiverTenantAccess(
          this.get('searchResult.personId'),
        );
      });
    }
    chain.then(
      () => {
        this.store
          .findRecord('person', this.get('searchResult.personId'))
          .then((person) => {
            this.step = 1;
            this.existingPerson = person;
          });
      },
      () => {
        this.errorMessage = `Unable to connect caregiver to ${this.session.school.name}.  Please contact Hero Support for assistance.`;
      },
    );
  }

  enableCaregiverTenantAccess(caregiverId) {
    let url = `${config.APP.apiHost}/people/v4/caregivers/${caregiverId}/:enableTenantAccess`;
    let data = {
      personId: caregiverId,
      schoolId: this.get('session.activeSchool'),
    };
    let options = {
      data: JSON.stringify(data),
    };
    return this.fetch.post(url, options);
  }

  @action
  cancel() {
    if (!isEmpty(this.existingPerson)) {
      // Undo any changes we've made to the "existing" person.
      this.existingPerson.rollbackAttributes();
    }
    this.onCancel();
  }

  @action
  merge() {
    this.incrementProperty('step');

    // We want to keep the "existing" person, as they already have the email
    // address and may therefore have logged in and generated content.  The
    // smoothest transition is therefore to merge the "current" person into
    // the "existing" one, so we'll apply any necessary field changes to the
    // "existing" record to push up as the new state.

    // First, push in any selected differences.
    filterBy(this.differences, 'selected', 'person').forEach((difference) => {
      difference.existingField.set(
        'value',
        difference.personField.get('value'),
      );
    });

    // Next, push in any fields which don't exist on the "existing" record.
    // These are likely to be this school's custom fields.
    this.extraFields.forEach((extraField) => {
      // note: extraFields is an array of POJOs, so `get` isn't required.
      this.existingPerson.addField(extraField.key, extraField.dataType);
      this.existingPerson.set(
        `fields.${extraField.key}.value`,
        extraField.value,
      );
    });

    this.merging = true;
    let data = {
      personId: this.existingPerson.id,
      duplicatePersonId: this.person.id,
      mergedPerson: this.existingPerson.serialize(),
    };
    let options = {
      data: JSON.stringify(data),
    };
    this.fetch
      .post(
        `${config.APP.apiHost}/people/v4/people/${this.get(
          'existingPerson.id',
        )}/:merge`,
        options,
      )
      .then(
        (merged) => {
          if (merged.person.id !== this.person.id) {
            this.mergedPersonId = merged.person.id;
            this.mergeComplete = true;
            this.merging = false;

            // Unload the "old" person record from the store to avoid confusing
            // browser history ("back" button showing the old record).
            this.store.unloadRecord(this.person);

            // Roll back our changes to the existing record, then push the merged
            // person into the store to ensure that Ember Data contains the most
            // up-to-date details for the caregiver post-merge.
            this.existingPerson.rollbackAttributes();
            this.store.pushPayload(merged);

            // Reload any already-loaded learner records, to avoid "blank"
            // contact cards for the "old" person record.
            let mergedPerson = this.store.peekRecord(
              'person',
              merged.person.id,
            );
            if (
              !isEmpty(mergedPerson.caregiver) &&
              !isEmpty(mergedPerson.caregiver.learnerIds)
            ) {
              mergedPerson.caregiver.learnerIds.forEach((learnerId) => {
                let learner = this.store.peekRecord('person', learnerId);
                if (!isEmpty(learner)) {
                  learner.reload();
                }
              });
            }
          } else {
            this.person.reload().then(() => {
              this.mergeComplete = true;
              this.merging = false;
            });
          }
        },
        () => {
          this.mergeFailed = true;
          this.merging = false;
        },
      );
  }

  @action
  done() {
    this.onDone(this.mergeComplete, this.mergedPersonId);
  }
}
